// import ResizeObserver from "resize-observer-polyfill" // Only if using polyfill
import { initSentry } from "./helpers/sentry"
import React from "react"
import ReactDOM from "react-dom"
import ApolloWrapper from "./containers/ApolloWrapper/ApolloWrapper"
import "./polyfills"
import { notification } from "antd"
import { InCommingAttributes } from "./types/filters"
import App, { ParentParams } from "./app/App"
import packageJson from "../package.json"
import moment from "moment-timezone"
import i18n from "./i18n"
import { loadExternalCss } from "./helpers/loadExternalCss"
import { MaintenanceProvider } from "./contexts/MaintenanceContext"
import TimezoneBootstrap from "./components/TimezoneBootstrap"
import { safeStorage } from "./helpers/safeStorage"
import { getParamObject } from "./helpers/iframeParams"
import { resolveLanguage } from "./helpers/resolveLanguage"
import { setStageFavicon } from "./helpers/setStageFavicon"

// Sentry so frueh wie moeglich initialisieren
initSentry()

// Stage-Tab am pinken Favicon erkennbar machen (No-op auf Prod)
setStageFavicon()

// Lade externe CSS-Datei, falls customCssUrl-Parameter vorhanden ist
loadExternalCss()

// Fallback-Timezone bis die dynamische Einstellung aus dem Backend geladen ist
const timeZontToUse = "Europe/Berlin"
moment.tz.setDefault(timeZontToUse)

// Resolve UI language (supports: en, de, de-DU, de-SIE) ----------------------
const navigatorLanguage = window.navigator.language
const fromStorage = safeStorage.getItem("i18n") || undefined
const i18nQueryParam = (() => {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.get("i18n") || undefined
    } catch {
        return undefined
    }
})()

// Normalisierung/Prioritätskette extrahiert nach helpers/resolveLanguage.ts
const resolvedLanguage = resolveLanguage({ queryParam: i18nQueryParam, stored: fromStorage, navigatorLanguage })
if (resolvedLanguage) {
    i18n.changeLanguage(resolvedLanguage)
    safeStorage.setItem("i18n", resolvedLanguage)
}

// SETUP locale for dates to show in the right language ----------------------
moment.locale(resolvedLanguage && resolvedLanguage.startsWith("de") ? "de" : "en-gb")

const iframeParameters: Partial<InCommingAttributes> = getParamObject([
    "city",
    "instructorId",
    "courseTypeId",
    "courseTypeIds",
    "locationId",
    "dayFilter",
    "courseCategoryId",
    "showFilterMenu",
    "showOnlyInWebActive",
    "listType",
    "i18n",
    "parentUrl",
    "customCssUrl",
    "cssUrl",
    "customCss",
    "maxWidth",
    "backTo",
]) as InCommingAttributes

// GETTING PARAMS FROM PARENT URL -----------------------------------------------------------------------------
// Die Deep-Link-Parameter (code/internalPath/courseId) stehen in der Query der
// EINBETTENDEN Seite und erreichen uns nur innerhalb von parentUrl. Neue Embeds
// encodieren parentUrl (dann steht z. B. "code%3D…" in der href und nur die
// dekodierte parentUrl enthaelt "code=…"), alte Embeds haengen sie unencodiert
// an (dann findet sie erst die Suche ueber die komplette href). Beide Quellen
// pruefen, erster Treffer gewinnt.
const decodedParentUrl = ((): string => {
    try {
        return new URLSearchParams(window.location.search).get("parentUrl") ?? ""
    } catch {
        return ""
    }
})()

const matchParentParam = (name: string): string | undefined => {
    for (const source of [decodedParentUrl, window.location.href]) {
        const value = source.match(new RegExp(`(\\?|&)${name}=([^&#]*)`))?.[2]
        if (value) return value
    }
    return undefined
}

// Anders als matchParentParam: ein vorhandener, aber leerer Parameter (?utm_source=) liefert "", nicht undefined.
const matchParentParamAllowEmpty = (name: string): string | undefined => {
    for (const source of [decodedParentUrl, window.location.href]) {
        const value = source.match(new RegExp(`(\\?|&)${name}=([^&#]*)`))?.[2]
        if (value !== undefined) return value
    }
    return undefined
}

const parentUrlParams: ParentParams = {
    code: matchParentParam("code"),
    internalPath: matchParentParam("internalPath"),
    courseId: matchParentParam("courseId"),
    // Rabattcode aus Flyer-/Newsletter-Link (?discountCode=…); `code` ist der Passwordless-Login-Code.
    discountCode: matchParentParam("discountCode"),
    // Herkunft des Links (?utm_source=kursseite von den öffentlichen Kursseiten), wird an der Buchung gespeichert.
    // Leer (?utm_source=) löscht eine gemerkte Herkunft, fehlend lässt sie stehen.
    utmSource: matchParentParamAllowEmpty("utm_source"),
}

// Add the ResizeObserver error suppression logic
if (typeof window !== "undefined") {
    const observerErr = () => {
        let resizeObserverErr = false
        window.addEventListener("error", (e) => {
            if (e.message === "ResizeObserver loop limit exceeded") {
                e.stopImmediatePropagation()
                resizeObserverErr = true
            }
        })
        window.addEventListener("unhandledrejection", (e) => {
            if (resizeObserverErr && e.reason === "ResizeObserver loop limit exceeded") {
                e.preventDefault()
            }
        })
    }
    observerErr()
}

// // --------------------------------------------------------------------------------------------------------
const start = async () => {
    ReactDOM.render(
        <ApolloWrapper>
            <TimezoneBootstrap>
                <MaintenanceProvider>
                    <App parameters={iframeParameters} version={packageJson.version} parentUrlParams={parentUrlParams} />
                </MaintenanceProvider>
            </TimezoneBootstrap>
        </ApolloWrapper>,
        document.getElementById("app")
    )
}
start().catch((error) => {
    notification.error({
        message: error.message,
    })
})
